import { useFetch } from '@/composables/useFetch';
import { usePlatform } from '@/composables/usePlatform';
import { APP_BUILD_NUMBER, APP_VERSION } from '@/config/version';
import { useEnvStore } from '@/stores/envStore';
import { App as CapacitorApp } from '@capacitor/app';
import * as Sentry from '@sentry/vue';
import { computed, ref } from 'vue';

export interface VersionCheckResponse {
  current_version: string;
  build_number: number;
  min_supported_version: string;
  needs_update: boolean;
  force_update: boolean;
  update_available: boolean;
  update_message: string;
  store_url: string | null;
  environment: string;
  last_deploy_date: string;
}

export interface CachedVersionData {
  response: VersionCheckResponse;
  timestamp: number;
  lastDismissed?: number;
}

const CACHE_KEY = 'version_check_cache';
const DISMISS_KEY = 'version_update_dismissed';
const CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds (debounce for navigation checks)
const DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const isChecking = ref(false);
const lastCheckTimestamp = ref<number>(0);
const versionCheckResponse = ref<VersionCheckResponse | null>(null);

export function useVersionCheck() {
  const { fetchData } = useFetch();
  const { isNative, platform, getStorage, setStorage } = usePlatform();
  const envStore = useEnvStore();

  /**
   * Get the native app version and build number from Capacitor
   */
  async function getNativeVersionInfo() {
    if (!isNative.value) {
      return {
        version: APP_VERSION,
        build: parseInt(APP_BUILD_NUMBER, 10),
      };
    }

    try {
      const appInfo = await CapacitorApp.getInfo();
      return {
        version: appInfo.version,
        build: parseInt(appInfo.build, 10),
      };
    } catch (error) {
      console.error('Failed to get native app info:', error);
      // Fallback to build-time version
      return {
        version: APP_VERSION,
        build: parseInt(APP_BUILD_NUMBER, 10),
      };
    }
  }

  /**
   * Load cached version check data
   */
  async function loadCachedData(): Promise<CachedVersionData | null> {
    try {
      const cached = await getStorage(CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Failed to load cached version data:', error);
    }
    return null;
  }

  /**
   * Save version check data to cache
   */
  async function saveCachedData(data: VersionCheckResponse) {
    try {
      const cacheData: CachedVersionData = {
        response: data,
        timestamp: Date.now(),
      };
      await setStorage(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Failed to cache version data:', error);
    }
  }

  /**
   * Check if user has recently dismissed an optional update
   */
  async function isDismissedRecently(): Promise<boolean> {
    try {
      const dismissed = await getStorage(DISMISS_KEY);
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const timeSinceDismissal = Date.now() - dismissedTime;
        return timeSinceDismissal < DISMISS_DURATION;
      }
    } catch (error) {
      console.error('Failed to check dismissal status:', error);
    }
    return false;
  }

  /**
   * Mark update as dismissed
   */
  async function dismissUpdate() {
    try {
      await setStorage(DISMISS_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to save dismissal:', error);
    }
  }

  /**
   * Check if we should perform a version check
   */
  function shouldCheckVersion(): boolean {
    if (isChecking.value) {
      return false;
    }

    const timeSinceLastCheck = Date.now() - lastCheckTimestamp.value;
    return timeSinceLastCheck >= CHECK_INTERVAL;
  }

  /**
   * Perform version check against the backend
   */
  async function checkVersion(force: boolean = false): Promise<VersionCheckResponse | null> {
    // Don't check if we recently checked (unless forced)
    if (!force && !shouldCheckVersion()) {
      return versionCheckResponse.value;
    }

    isChecking.value = true;

    try {
      const versionInfo = await getNativeVersionInfo();
      const url = `${envStore.apiUrl}/version/check`;

      // Add version headers - these will be automatically added by makeRequest
      // but we log them for debugging
      console.log('Version check:', {
        clientVersion: versionInfo.version,
        clientBuild: versionInfo.build,
        platform: platform.value,
      });

      const { data, error, statusCode } = await fetchData(url, 'GET');

      if (error || statusCode !== 200) {
        // If offline or error, try to use cached data
        const cached = await loadCachedData();
        if (cached) {
          console.log('Using cached version data due to error');
          versionCheckResponse.value = cached.response;
          return cached.response;
        }

        // Log error to Sentry
        Sentry.captureException(new Error('Version check failed'), {
          extra: {
            statusCode,
            error,
            clientVersion: versionInfo.version,
            clientBuild: versionInfo.build,
          },
        });

        return null;
      }

      const response = data as VersionCheckResponse;
      versionCheckResponse.value = response;
      lastCheckTimestamp.value = Date.now();

      // Cache the response
      await saveCachedData(response);

      // Log version mismatch to Sentry if needed
      if (response.needs_update || response.update_available) {
        Sentry.captureMessage('Version mismatch detected', {
          level: response.needs_update ? 'warning' : 'info',
          extra: {
            clientVersion: versionInfo.version,
            serverVersion: response.current_version,
            minSupportedVersion: response.min_supported_version,
            needsUpdate: response.needs_update,
            forceUpdate: response.force_update,
          },
        });
      }

      return response;
    } catch (error) {
      console.error('Version check error:', error);
      Sentry.captureException(error);

      // Try to use cached data as fallback
      const cached = await loadCachedData();
      if (cached) {
        versionCheckResponse.value = cached.response;
        return cached.response;
      }

      return null;
    } finally {
      isChecking.value = false;
    }
  }

  /**
   * Check if update dialog should be shown
   */
  async function shouldShowUpdateDialog(): Promise<boolean> {
    // Don't show update dialog for web apps
    if (!isNative.value) {
      console.log('[shouldShowUpdateDialog] Web app - not showing update dialog');
      return false;
    }

    if (!versionCheckResponse.value) {
      console.log('[shouldShowUpdateDialog] No version response available');
      return false;
    }

    const response = versionCheckResponse.value;
    try {
      const payload = {
        force_update: response.force_update,
        needs_update: response.needs_update,
        update_available: response.update_available,
        current_version: response.current_version,
        min_supported_version: response.min_supported_version,
      };
      // stringify so Android logs don't show "[Object object]"
      console.log('[shouldShowUpdateDialog] Version response: ' + JSON.stringify(payload));
    } catch (err) {
      console.log('[shouldShowUpdateDialog] Version response (stringify failed):', String(err));
    }

    // Always show if force update is required
    if (response.force_update || response.needs_update) {
      console.log('[shouldShowUpdateDialog] Force/required update - showing dialog');
      return true;
    }

    // For optional updates, check if user dismissed recently
    if (response.update_available) {
      const dismissed = await isDismissedRecently();
      console.log('[shouldShowUpdateDialog] Optional update available, dismissed:', dismissed);
      return !dismissed;
    }

    console.log('[shouldShowUpdateDialog] No update needed');
    return false;
  }

  /**
   * Initialize version checking on app start
   */
  async function initVersionCheck() {
    // Load cached data immediately for offline scenarios
    const cached = await loadCachedData();
    if (cached) {
      versionCheckResponse.value = cached.response;
      lastCheckTimestamp.value = cached.timestamp;
    }

    // Perform actual check
    await checkVersion(true);
  }

  const needsUpdate = computed(() => versionCheckResponse.value?.needs_update ?? false);
  const forceUpdate = computed(() => versionCheckResponse.value?.force_update ?? false);
  const updateAvailable = computed(() => versionCheckResponse.value?.update_available ?? false);
  const updateMessage = computed(() => versionCheckResponse.value?.update_message ?? '');
  const storeUrl = computed(() => versionCheckResponse.value?.store_url ?? null);

  return {
    // State
    isChecking,
    versionCheckResponse,
    needsUpdate,
    forceUpdate,
    updateAvailable,
    updateMessage,
    storeUrl,

    // Methods
    checkVersion,
    initVersionCheck,
    shouldShowUpdateDialog,
    dismissUpdate,
    getNativeVersionInfo,
  };
}
